import * as React from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useDesignSystemLabels } from './labels';

/**
 * Dialog — shadcn (base-vega) on Base UI.
 *
 * Migration mapping (Radix → Base UI):
 *   Dialog.Root        → Dialog.Root
 *   Dialog.Trigger     → Dialog.Trigger
 *   Dialog.Portal      → Dialog.Portal
 *   Dialog.Overlay     → Dialog.Backdrop
 *   Dialog.Content     → Dialog.Popup        (also wrapped in Backdrop)
 *   Dialog.Title       → Dialog.Title
 *   Dialog.Description → Dialog.Description
 *   Dialog.Close       → Dialog.Close
 *
 * Note: Base UI swaps `data-state="open|closed"` for boolean
 * `data-open` / `data-closed` markers; the animation classes were updated
 * to target those instead.
 */
const Dialog = BaseDialog.Root;
const DialogTrigger = BaseDialog.Trigger;
const DialogPortal = BaseDialog.Portal;
const DialogClose = BaseDialog.Close;

const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Backdrop>
>(({ className, ...props }, ref) => (
  <BaseDialog.Backdrop
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-background/80 backdrop-blur-sm',
      'data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Popup>
>(({ className, children, ...props }, ref) => {
  const { close } = useDesignSystemLabels();
  return (
    <DialogPortal>
      <DialogOverlay />
      <BaseDialog.Popup
        ref={ref}
        className={cn(
          'fixed start-1/2 top-1/2 z-50 grid max-h-[calc(100dvh-2rem)] w-full max-w-lg -translate-y-1/2 gap-4 overflow-y-auto border border-border bg-card p-6 shadow-lg duration-200 sm:rounded-lg',
          'rtl:translate-x-1/2 ltr:-translate-x-1/2',
          'data-[open]:animate-in data-[closed]:animate-out',
          className,
        )}
        {...props}
      >
        {children}
        <BaseDialog.Close
          className="absolute end-4 top-4 rounded-sm text-[var(--color-foreground-3)] ring-offset-background transition-colors hover:text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          aria-label={close}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </BaseDialog.Close>
      </BaseDialog.Popup>
    </DialogPortal>
  );
});
DialogContent.displayName = 'DialogContent';

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5 text-center sm:text-start', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 rtl:sm:space-x-reverse',
        className,
      )}
      {...props}
    />
  );
}

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Title>
>(({ className, ...props }, ref) => (
  <BaseDialog.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Description>
>(({ className, ...props }, ref) => (
  <BaseDialog.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
